import { SPAN_KINDS, type SpanKind } from '@/domain';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { CloseIcon } from '@/ui/icons';
import fields from './IntakeScreen.module.css';
import styles from './BridgesFields.module.css';

const KIND_LABEL: Record<SpanKind, string> = {
  bridge: strings.intake.spanBridge,
  titaniumBar: strings.intake.spanBar,
};

/** One row of the section: a span being edited, with a key for React. */
export interface SpanRow {
  key: string;
  name: string;
  kind: SpanKind;
  from: string;
  to: string;
}

/**
 * Bridges & bars, the section under Item per tooth.
 *
 * Present whether or not a tooth is marked, because the form has to say the
 * feature exists. The from and to selects offer only the teeth marked on the
 * chart, in arch order, so a span can only be drawn between teeth the case
 * actually has; a span whose teeth are unmarked afterwards shows its row in
 * the error state until it is fixed or removed. The chart redraws as the rows
 * change, which is the whole point of the section sitting under it.
 */
export function BridgesFields({
  rows,
  teeth,
  errors,
  onChange,
}: {
  rows: SpanRow[];
  /** The marked teeth, in arch order. */
  teeth: readonly string[];
  /** One entry per row, null where the row is sound. */
  errors: readonly (string | null)[];
  onChange: (rows: SpanRow[]) => void;
}) {
  function update(index: number, patch: Partial<SpanRow>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  return (
    <div className={fields.field}>
      <span className={fields.l}>
        {strings.intake.bridges}
        <span className={fields.lock}>{strings.intake.bridgesHint}</span>
      </span>

      {rows.length > 0 && (
        <div className={styles.heads} aria-hidden="true">
          <span>{strings.intake.spanType}</span>
          <span>{strings.intake.spanName}</span>
          <span>{strings.intake.spanFrom}</span>
          <span>{strings.intake.spanTo}</span>
          <span />
        </div>
      )}

      {rows.map((row, index) => {
        const error = errors[index] ?? null;
        const errorId = `${row.key}-error`;
        return (
          <div key={row.key} className={`${styles.row} ${error === null ? '' : styles.rowError}`}>
            <select
              className={`${fields.select} ${styles.kind}`}
              aria-label={`${strings.intake.spanType} ${String(index + 1)}`}
              value={row.kind}
              onChange={(event) => update(index, { kind: event.target.value as SpanKind })}
            >
              {SPAN_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {KIND_LABEL[kind]}
                </option>
              ))}
            </select>

            <input
              className={`${fields.input} ${styles.name}`}
              aria-label={`${strings.intake.spanName} ${String(index + 1)}`}
              aria-invalid={error !== null}
              aria-describedby={error === null ? undefined : errorId}
              value={row.name}
              placeholder={strings.intake.spanNamePlaceholder}
              maxLength={60}
              onChange={(event) => update(index, { name: event.target.value })}
            />

            <span className={`${styles.end} ${styles.from}`}>
              <span className={styles.endLabel}>{strings.intake.spanFrom}</span>
              <ToothSelect
                label={`${strings.intake.spanFrom} ${String(index + 1)}`}
                value={row.from}
                teeth={teeth}
                onChange={(from) => update(index, { from })}
              />
            </span>
            <span className={`${styles.end} ${styles.to}`}>
              <span className={styles.endLabel}>{strings.intake.spanTo}</span>
              <ToothSelect
                label={`${strings.intake.spanTo} ${String(index + 1)}`}
                value={row.to}
                teeth={teeth}
                onChange={(to) => update(index, { to })}
              />
            </span>

            <button
              type="button"
              className={styles.remove}
              aria-label={`${strings.intake.removeSpan} ${KIND_LABEL[row.kind].toLowerCase()} ${String(index + 1)}`}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              <CloseIcon />
            </button>

            {error !== null && (
              <span id={errorId} className={styles.error} role="alert">
                {error}
              </span>
            )}
          </div>
        );
      })}

      <div className={styles.actions}>
        <Button type="button" onClick={() => onChange([...rows, newRow('bridge', teeth)])}>
          {strings.intake.addBridge}
        </Button>
        <Button type="button" onClick={() => onChange([...rows, newRow('titaniumBar', teeth)])}>
          {strings.intake.addBar}
        </Button>
      </div>
    </div>
  );
}

let rowSequence = 0;

/**
 * A fresh row: an empty name and the first two marked teeth of the upper arch
 * preselected, which on most cases is the bridge reception is about to name.
 * A case with fewer than two upper teeth takes the first two marked anywhere,
 * and a case with nothing marked yet leaves both ends empty.
 */
function newRow(kind: SpanKind, teeth: readonly string[]): SpanRow {
  const upper = teeth.filter((tooth) => tooth.startsWith('1') || tooth.startsWith('2'));
  const pool = upper.length >= 2 ? upper : teeth;
  rowSequence += 1;
  return {
    key: `span-${String(rowSequence)}`,
    name: '',
    kind,
    from: pool[0] ?? '',
    to: pool[1] ?? pool[0] ?? '',
  };
}

function ToothSelect({
  label,
  value,
  teeth,
  onChange,
}: {
  label: string;
  value: string;
  teeth: readonly string[];
  onChange: (tooth: string) => void;
}) {
  // A tooth unmarked after it was chosen stays in the list, flagged by the
  // row's error, so the select does not silently show a different tooth.
  const options = teeth.includes(value) || value === '' ? teeth : [value, ...teeth];
  return (
    <select
      className={`${fields.select} ${styles.tooth}`}
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {value === '' && <option value="" />}
      {options.map((tooth) => (
        <option key={tooth} value={tooth}>
          {tooth}
        </option>
      ))}
    </select>
  );
}
